import { rufe } from './client';

/**
 * Rechtstexte, Zustimmung und der Stand einer Buchung (BUC-25, BUC-123).
 *
 * DIE VERSIONSKENNUNG WIRD NIE MITGESCHICKT. Der Dienst liest sie aus der
 * Fassung — genau dieses Protokoll soll im Streitfall gelten, und dürfte die
 * Oberfläche die Kennung mitgeben, stünde darin, was sie behauptet, nicht,
 * was gilt.
 */

export type Empfaengerweg = 'anmelder' | 'nach_kundenart';

export type Rechtstextart = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly empfaengerweg: Empfaengerweg;
  readonly zustimmungspflichtig: boolean;
  readonly nurVerbraucher: boolean;
  readonly reihenfolge: number;
  /** BUC-78: ob die Fassung als Datei angehängt wird. */
  readonly alsAnhang: boolean;
  /** Ob ihr Wortlaut zusätzlich im Nachrichtenkörper steht. */
  readonly imMailtext: boolean;
};

export type Mailvorlage = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly anlass: string;
  readonly kundenart: string | null;
  readonly bezeichnung: string;
  readonly betreff: string;
  readonly textRein: string;
  readonly textHtml: string;
  readonly aktiv: boolean;
  readonly abgeloestAm: string | null;
};

export type Mailplatzhalter = {
  readonly anlass: string;
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly beispiel: string | null;
  readonly pflicht: boolean;
};

export type Fassung = {
  readonly id: string;
  readonly stamm: string;
  readonly version: number;
  readonly art: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly artikel: string | null;
  readonly artikelbezeichnung: string | null;
  readonly inhaltHtml: string;
  readonly ablageschluessel: string | null;
  readonly dateiname: string | null;
  readonly gueltigAb: string;
  readonly abgeloestAm: string | null;
  readonly platzhalter: boolean;
};

export type GeltenderText = {
  readonly id: string;
  readonly art: string;
  readonly bezeichnung: string;
  readonly kennung: string;
  readonly version: number;
  readonly empfaengerweg: Empfaengerweg;
  readonly zustimmungspflichtig: boolean;
  readonly platzhalter: boolean;
  readonly ablageschluessel: string | null;
  readonly alsAnhang: boolean;
  readonly imMailtext: boolean;
};

export type Zustimmung = {
  readonly id: string;
  readonly gegenstand: 'rechtstext' | 'daten_dritter' | 'vorzeitiger_beginn';
  readonly rechtstext: string | null;
  readonly wortlaut: string;
  readonly versionskennung: string;
  readonly erteiltAm: string;
  readonly weg: string | null;
};

export type Zustimmungsstand = {
  readonly offen: readonly GeltenderText[];
  readonly datenDritterOffen: boolean;
  readonly vorzeitigerBeginnOffen: boolean;
  readonly platzhalter: readonly GeltenderText[];
};

export async function arten(): Promise<readonly Rechtstextart[]> {
  const antwort = await rufe<{ readonly arten: readonly Rechtstextart[] }>('/rechtstexte/arten');
  return antwort.arten;
}

export async function fassungen(auchAbgeloeste = false): Promise<readonly Fassung[]> {
  const fragen = new URLSearchParams();
  if (auchAbgeloeste) fragen.set('auchAbgeloeste', 'true');
  const antwort = await rufe<{ readonly fassungen: readonly Fassung[] }>(
    `/rechtstexte?${fragen.toString()}`,
  );
  return antwort.fassungen;
}

/**
 * Eine neue Fassung einstellen; die bisherige wird dabei abgelöst.
 *
 * Es gibt bewusst kein Ändern. An einer Fassung hängen protokollierte
 * Zustimmungen (BUC-25).
 */
export async function neueFassung(eingabe: {
  readonly stamm?: string;
  readonly art: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly artikel: string | null;
  readonly inhaltHtml: string;
  readonly gueltigAb?: string;
  readonly platzhalter: boolean;
}): Promise<{ readonly id: string; readonly stamm: string; readonly version: number }> {
  return rufe('/rechtstexte', { verfahren: 'POST', rumpf: eingabe });
}

export async function fuerBuchung(buchung: string): Promise<readonly GeltenderText[]> {
  const antwort = await rufe<{ readonly texte: readonly GeltenderText[] }>(
    `/rechtstexte/buchung/${buchung}`,
  );
  return antwort.texte;
}

export async function stand(buchung: string): Promise<Zustimmungsstand> {
  return rufe(`/rechtstexte/buchung/${buchung}/stand`);
}

export async function zustimmungen(buchung: string): Promise<readonly Zustimmung[]> {
  const antwort = await rufe<{ readonly zustimmungen: readonly Zustimmung[] }>(
    `/rechtstexte/buchung/${buchung}/zustimmungen`,
  );
  return antwort.zustimmungen;
}

export async function stimmeZu(
  buchung: string,
  rechtstext: string,
  weg: 'portal' | 'innendienst' | 'angebot' = 'innendienst',
): Promise<{ readonly stand: Zustimmungsstand }> {
  return rufe(`/rechtstexte/buchung/${buchung}/zustimmung`, {
    verfahren: 'POST',
    rumpf: { rechtstext, weg },
  });
}

export async function bestaetige(
  buchung: string,
  gegenstand: 'daten_dritter' | 'vorzeitiger_beginn',
  weg: 'portal' | 'innendienst' | 'angebot' = 'innendienst',
): Promise<{ readonly stand: Zustimmungsstand }> {
  return rufe(`/rechtstexte/buchung/${buchung}/bestaetigung`, {
    verfahren: 'POST',
    rumpf: { gegenstand, weg },
  });
}

export type Versandzeile = {
  readonly rechtstext: string;
  readonly bezeichnung: string;
  readonly kennung: string;
  readonly empfaenger: string;
  readonly rolle: 'anmelder' | 'teilnehmer';
};

export type Versandvorschau = {
  readonly zeilen: readonly Versandzeile[];
  readonly platzhalter: readonly string[];
  readonly ohneAdresse: readonly string[];
  readonly rueckfallAufAnmelder: boolean;
  readonly ohneDatei: readonly string[];
  readonly anhaenge: readonly string[];
  readonly imMailtext: readonly string[];
  /** Der Probeversand ist eingeschaltet — es ginge unfertig hinaus. */
  readonly probeversand: boolean;
};

export type Nachzuholen = {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly versionskennung: string;
  readonly empfaenger: string;
  readonly versandtAm: string;
};

/**
 * Der Probeversand: Zustand und was er angerichtet hat.
 *
 * Beides in einer Antwort — wer den Schalter sieht, muss zugleich sehen, wem
 * nur Platzhalter zugegangen sind.
 */
export async function probeversand(): Promise<{
  readonly erlaubt: boolean;
  readonly nachzuholen: readonly Nachzuholen[];
}> {
  return rufe('/rechtstexte/probeversand');
}

export async function probeversandSetzen(
  erlaubt: boolean,
): Promise<{ readonly erlaubt: boolean }> {
  return rufe('/rechtstexte/probeversand', { verfahren: 'PUT', rumpf: { erlaubt } });
}

export async function versandvorschau(buchung: string): Promise<Versandvorschau> {
  return rufe(`/rechtstexte/buchung/${buchung}/versandvorschau`);
}

/**
 * Die Rechtstexte verschicken (BUC-78).
 *
 * `anlass: 'erneut'` ist die eine Stelle, an der eine Dublette gewollt ist —
 * sonst schickt zweimal Drücken einmal.
 */
export async function verschicke(
  buchung: string,
  anlass: 'buchung' | 'telefonat' | 'erneut' = 'buchung',
): Promise<{
  readonly nachrichten: number;
  readonly empfaenger: readonly string[];
  readonly rueckfallAufAnmelder: boolean;
  readonly probe: boolean;
}> {
  return rufe(`/rechtstexte/buchung/${buchung}/versand`, {
    verfahren: 'POST',
    rumpf: { anlass },
  });
}

export type Versandvermerk = {
  readonly rechtstext: string;
  readonly versionskennung: string;
  readonly empfaenger: string;
  readonly rolle: string;
  readonly versandtAm: string;
};

export async function versandvermerke(buchung: string): Promise<readonly Versandvermerk[]> {
  const antwort = await rufe<{ readonly vermerke: readonly Versandvermerk[] }>(
    `/rechtstexte/buchung/${buchung}/versandvermerke`,
  );
  return antwort.vermerke;
}


/**
 * Die Wege einer Textart setzen (BUC-78).
 *
 * Mindestens einer von beiden — eine Art, die weder angehängt wird noch im
 * Text steht, würde übermittelt, ohne übermittelt zu werden. Die Datenbank
 * weist es ab, die Maske lässt es gar nicht erst zu.
 */
export async function wegeSetzen(
  schluessel: string,
  wege: {
    readonly alsAnhang: boolean;
    readonly imMailtext: boolean;
    readonly empfaengerweg: Empfaengerweg;
  },
): Promise<{ readonly arten: readonly Rechtstextart[] }> {
  return rufe(`/rechtstexte/arten/${schluessel}`, { verfahren: 'PUT', rumpf: wege });
}

export async function mailvorlagen(auchAbgeloeste = false): Promise<readonly Mailvorlage[]> {
  const fragen = new URLSearchParams();
  if (auchAbgeloeste) fragen.set('auchAbgeloeste', 'true');
  const antwort = await rufe<{ readonly vorlagen: readonly Mailvorlage[] }>(
    `/rechtstexte/mailvorlagen?${fragen.toString()}`,
  );
  return antwort.vorlagen;
}

export async function mailplatzhalter(
  anlass = 'bedingungen',
): Promise<readonly Mailplatzhalter[]> {
  const antwort = await rufe<{ readonly platzhalter: readonly Mailplatzhalter[] }>(
    `/rechtstexte/mailplatzhalter?anlass=${anlass}`,
  );
  return antwort.platzhalter;
}

export async function neueMailvorlage(eingabe: {
  readonly stamm?: string;
  readonly anlass: string;
  readonly kundenart: string | null;
  readonly bezeichnung: string;
  readonly betreff: string;
  readonly textRein: string;
  readonly textHtml: string;
}): Promise<{ readonly id: string; readonly stamm: string; readonly version: number }> {
  return rufe('/rechtstexte/mailvorlagen', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die PDF-Datei zu einer Fassung hinterlegen (BUC-78, SYS-07).
 *
 * Als Base64 im Rumpf: Der Rest dieser Schnittstelle spricht JSON, und ein
 * zweiter Aufnahmeweg brächte einen zweiten Fehlerpfad.
 */
export async function dateiHinterlegen(
  fassung: string,
  datei: File,
): Promise<{ readonly pruefsumme: string; readonly groesse: number }> {
  const puffer = await datei.arrayBuffer();
  let binaer = '';
  const bytes = new Uint8Array(puffer);
  /* In Stücken, weil `String.fromCharCode(...bytes)` bei großen Dateien den
   * Aufrufstapel sprengt. */
  for (let i = 0; i < bytes.length; i += 8192) {
    binaer += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return rufe(`/rechtstexte/${fassung}/datei`, {
    verfahren: 'POST',
    rumpf: { dateiname: datei.name, inhaltBase64: btoa(binaer) },
  });
}
